import type { AlcancePromocion, ReglaDePromocion } from '@tanteo/compartido';
import { supabase } from './supabase';
import { ErrorDeApi } from './error-api';

/** Los descuentos que el dueño configura: lo que existe aquí es lo único que el mostrador ofrece. */

export type FormaDescuento = 'porcentaje' | 'monto_fijo' | 'libre' | 'lleva_paga';

export interface DescuentoDelNegocio extends ReglaDePromocion {
  forma: FormaDescuento;
  /** El porcentaje o el monto fijo; null en el libre, que se escribe en el mostrador, y en lleva y paga. */
  valor: number | null;
  lleva: number | null;
  paga: number | null;
  maxVeces: number | null;
  maxUnidades: number | null;
  /** Con días o fechas: el mostrador lo aplica solo a lo que cubre. */
  automatico: boolean;
  enProducto: boolean;
  enVenta: boolean;
  paraPropietario: boolean;
  paraCajero: boolean;
}

export type DescuentoNuevo = Omit<DescuentoDelNegocio, 'id' | 'activo' | 'automatico'>;

function mensajeDescuento(codigo: string | undefined, crudo: string): string {
  if (codigo === '23505') return 'Ya tienes un descuento con ese nombre, o ya existe el descuento libre.';
  if (codigo === '23514') return 'Revisa el descuento: necesita un valor válido, dónde se usa y quién puede darlo.';
  return crudo;
}

export async function listarDescuentosNegocio(negocioId: string): Promise<DescuentoDelNegocio[]> {
  const { data, error, status } = await supabase
    .from('descuentos_negocio')
    .select(
      'id, descuento_nombre, descuento_forma, descuento_porcentaje, descuento_monto_fijo, descuento_lleva, descuento_paga, descuento_desde, descuento_hasta, descuento_dias, descuento_max_veces, descuento_max_unidades, descuento_es_automatico, descuento_en_producto, descuento_en_venta, descuento_para_propietario, descuento_para_cajero, descuento_activo, descuentos_alcance(producto_id, categoria)'
    )
    .eq('negocio_id', negocioId)
    .is('descuento_eliminado_en', null)
    .order('creado_en')
    .order('descuento_nombre');
  if (error) throw new ErrorDeApi(error, status);
  return (data ?? []).map((d) => ({
    id: d.id,
    nombre: d.descuento_nombre,
    forma: d.descuento_forma as FormaDescuento,
    valor:
      d.descuento_forma === 'porcentaje'
        ? Number(d.descuento_porcentaje)
        : d.descuento_forma === 'monto_fijo'
          ? Number(d.descuento_monto_fijo)
          : null,
    lleva: d.descuento_lleva,
    paga: d.descuento_paga,
    desde: d.descuento_desde,
    hasta: d.descuento_hasta,
    dias: d.descuento_dias ?? [],
    maxVeces: d.descuento_max_veces,
    maxUnidades: d.descuento_max_unidades,
    automatico: d.descuento_es_automatico,
    alcance: ((d.descuentos_alcance ?? []) as { producto_id: string | null; categoria: string | null }[]).map(
      (a): AlcancePromocion => ({ productoId: a.producto_id, categoria: a.categoria })
    ),
    enProducto: d.descuento_en_producto,
    enVenta: d.descuento_en_venta,
    paraPropietario: d.descuento_para_propietario,
    paraCajero: d.descuento_para_cajero,
    activo: d.descuento_activo,
  }));
}

/** Crea el descuento, o reemplaza el de `id` por uno nuevo: lo vendido, y lo que espera sin conexión, conserva el viejo. */
export async function guardarDescuentoNegocio(negocioId: string, id: string | null, d: DescuentoNuevo): Promise<void> {
  const { error, status } = await supabase.rpc('guardar_descuento_negocio', {
    p_negocio: negocioId,
    p_descuento_id: id,
    p_datos: {
      nombre: d.nombre,
      forma: d.forma,
      porcentaje: d.forma === 'porcentaje' ? d.valor : null,
      monto_fijo: d.forma === 'monto_fijo' ? d.valor : null,
      lleva: d.forma === 'lleva_paga' ? d.lleva : null,
      paga: d.forma === 'lleva_paga' ? d.paga : null,
      desde: d.desde,
      hasta: d.hasta,
      dias: d.dias,
      max_veces: d.maxVeces,
      max_unidades: d.maxUnidades,
      en_producto: d.enProducto,
      en_venta: d.enVenta,
      para_propietario: d.paraPropietario,
      para_cajero: d.paraCajero,
    },
    p_alcance: d.alcance.map((a) => ({ producto_id: a.productoId, categoria: a.categoria })),
  });
  if (error) throw error.code === '23505' || error.code === '23514' ? new Error(mensajeDescuento(error.code, error.message)) : new ErrorDeApi(error, status);
}

export async function cambiarEstadoDescuentoNegocio(negocioId: string, id: string, activo: boolean): Promise<void> {
  const { error, status } = await supabase
    .from('descuentos_negocio')
    .update({ descuento_activo: activo })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}

/** Borrado lógico y sin vuelta: sale del mostrador y de esta lista. */
export async function eliminarDescuentoNegocio(negocioId: string, id: string): Promise<void> {
  const { error, status } = await supabase
    .from('descuentos_negocio')
    .update({ descuento_eliminado_en: new Date().toISOString() })
    .eq('negocio_id', negocioId)
    .eq('id', id);
  if (error) throw new ErrorDeApi(error, status);
}
